// src/hooks/useTaskUrlSync.js
import { useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import useTaskStore from '../stores/taskStore';

/**
 * Custom hook to synchronize task drawer state with URL parameters
 * Handles opening/closing task drawer based on URL and updates URL when drawer state changes
 */
export const useTaskUrlSync = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { 
    isTaskDrawerOpen, 
    currentTask,
    openTaskDrawer, 
    closeTaskDrawer 
  } = useTaskStore();
  
  // Track if we're currently syncing to prevent loops
  const isSyncingRef = useRef(false);
  const lastUrlTaskIdRef = useRef(null);
  const lastDrawerStateRef = useRef(false);
  
  // Handle URL changes (e.g., direct navigation, back/forward)
  useEffect(() => {
    const taskId = searchParams.get('taskId');
    
    // Skip if we're already syncing or if nothing changed
    if (isSyncingRef.current || taskId === lastUrlTaskIdRef.current) {
      return;
    }
    
    lastUrlTaskIdRef.current = taskId;
    
    if (taskId && !isTaskDrawerOpen) {
      // URL has taskId but drawer is closed - open it
      console.log('[useTaskUrlSync] Opening drawer for taskId:', taskId);
      isSyncingRef.current = true;
      openTaskDrawer(taskId);
      // Give more time for the task to load before allowing more sync operations
      setTimeout(() => { isSyncingRef.current = false; }, 500);
    } else if (!taskId && isTaskDrawerOpen && !isSyncingRef.current) {
      // URL doesn't have taskId but drawer is open - close it
      // Only close if we're not in the middle of opening a task
      console.log('[useTaskUrlSync] Closing drawer - no taskId in URL');
      isSyncingRef.current = true;
      closeTaskDrawer();
      setTimeout(() => { isSyncingRef.current = false; }, 100);
    }
  }, [searchParams, isTaskDrawerOpen, openTaskDrawer, closeTaskDrawer]);
  
  // Handle drawer state changes
  useEffect(() => {
    // Skip if we're already syncing
    if (isSyncingRef.current) {
      return;
    }
    
    const taskId = searchParams.get('taskId');
    
    // Only update URL when drawer is closed, not when it's opened
    // This prevents the circular updates that cause the drawer to close
    if (!isTaskDrawerOpen && taskId) {
      // Drawer is closed but URL has taskId - remove it
      console.log('[useTaskUrlSync] Removing taskId from URL - drawer closed');
      isSyncingRef.current = true;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('taskId');
      setSearchParams(newSearchParams, { replace: true });
      setTimeout(() => { isSyncingRef.current = false; }, 100);
    }
  }, [isTaskDrawerOpen, searchParams, setSearchParams]);
  
  // Generate shareable link for a task
  const getShareableTaskLink = useCallback((taskId) => {
    const baseUrl = window.location.origin;
    const currentPath = location.pathname;
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('taskId', taskId);
    
    return `${baseUrl}${currentPath}?${newSearchParams.toString()}`;
  }, [location.pathname, searchParams]);
  
  // Copy shareable link to clipboard
  const copyTaskLink = useCallback(async (taskId) => {
    const link = getShareableTaskLink(taskId);
    
    try {
      await navigator.clipboard.writeText(link);
      return true;
    } catch (error) {
      console.error('Failed to copy link:', error);
      return false;
    }
  }, [getShareableTaskLink]);
  
  return {
    getShareableTaskLink,
    copyTaskLink
  };
};